import { Button, IconButton, Alert, FormField, TextInput, SuccessNotice } from "@a2z/ui";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { StoreHighlightView } from "@/lib/api/types";
import { useUpdateStoreMutation } from "@/modules/settings/services/useUpdateStoreMutation";

type HighlightItem = {
  id: string;
  textAr: string;
  textEn: string;
};

type StoreHighlightsFormProps = {
  storeSlug: string;
  initialHighlights: StoreHighlightView[];
  /**
   * When provided the form runs in "sheet" mode:
   * - No viewport-fixed floating save bar is rendered.
   * - The ref is populated with the submit function so the parent sheet
   *   can invoke it from its own sticky footer.
   */
  submitRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  /** Called whenever the dirty state changes — lets the sheet footer know. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Called whenever the submitting state changes — lets the sheet footer disable its button. */
  onSubmittingChange?: (submitting: boolean) => void;
};

const emptyHighlight = (): HighlightItem => ({
  id: globalThis.crypto?.randomUUID?.() ?? `new-${Date.now()}-${Math.random()}`,
  textAr: "",
  textEn: "",
});

function serializeHighlights(items: HighlightItem[]): string {
  return JSON.stringify(
    items.map((item) => ({
      textAr: item.textAr.trim(),
      textEn: item.textEn.trim(),
    })),
  );
}

// ─── Icon components ──────────────────────────────────────────────────────────

function ChevronUpIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function HighlightsForm({
  storeSlug,
  initialHighlights,
  submitRef,
  onDirtyChange,
  onSubmittingChange,
}: StoreHighlightsFormProps) {
  const isSheetMode = submitRef !== undefined;
  const t = useTranslations();
  const initialItems = useMemo(
    () =>
      [...initialHighlights]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((highlight) => ({
          id: highlight.id,
          textAr: highlight.textAr,
          textEn: highlight.textEn ?? "",
        })),
    [initialHighlights],
  );
  const [items, setItems] = useState<HighlightItem[]>(initialItems);

  const [initialSnapshot, setInitialSnapshot] = useState(() => ({
    serialized: serializeHighlights(initialItems),
    length: initialItems.length,
  }));
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: updateStore } = useUpdateStoreMutation();

  // Tracks which items have the optional EN field expanded.
  // An item's EN field is always shown if it already contains text — this
  // ensures data from the server is never hidden on load.
  const [expandedEn, setExpandedEn] = useState<Set<string>>(
    () => new Set(initialHighlights.filter((h) => (h.textEn ?? "").trim()).map((h) => h.id)),
  );

  const currentSnapshot = serializeHighlights(items);
  const isDirty =
    currentSnapshot !== initialSnapshot.serialized ||
    items.length !== initialSnapshot.length;

  // Sync state to parent sheet so it can drive its sticky footer.
  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);
  useEffect(() => { onSubmittingChange?.(isSubmitting); }, [isSubmitting, onSubmittingChange]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- submitRef is assigned after render, when handleSubmit is already defined
    if (submitRef) submitRef.current = handleSubmit;
  });

  const showEnField = (item: HighlightItem) =>
    expandedEn.has(item.id) || item.textEn.trim().length > 0;

  const handleExpandEn = (id: string) => {
    setExpandedEn((prev) => new Set([...prev, id]));
  };

  const handleAddHighlight = () => {
    setItems((prev) => [...prev, emptyHighlight()]);
    setSuccess(false);
  };

  const handleRemoveHighlight = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    setSuccess(false);
  };

  const handleChange = (id: string, field: "textAr" | "textEn", value: string) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
    setSuccess(false);
  };

  const moveItem = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setSuccess(false);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(false);
    setIsSubmitting(true);
    const payload = items.map((item) => ({
      textAr: item.textAr,
      textEn: item.textEn,
    }));
    try {
      await updateStore({ storeSlug, highlights: payload });
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : t(keys.errors.generic));
      setIsSubmitting(false);
      return;
    }
    setInitialSnapshot({ serialized: currentSnapshot, length: items.length });
    setSuccess(true);
    setIsSubmitting(false);
  };

  return (
    <>
      {/* Extra bottom padding so floating save bar never hides content (page mode only) */}
      <div className={["flex w-full flex-col gap-4", !isSheetMode && isDirty ? "pb-24" : ""].join(" ")}>
        {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
        {success ? <SuccessNotice title={t(keys.common.updateSuccess)} /> : null}

        <div className="flex flex-col gap-3">
          <p className="text-sm text-[color:var(--text-muted)]">
            {t(keys.seller.storeHighlightsHelper)}
          </p>

          {items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[color:var(--border-light)] px-4 py-5 text-sm text-[color:var(--text-muted)]">
              {t(keys.seller.storeHighlightsEmpty)}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4"
                >
                  {/* AR field — always visible */}
                  <FormField
                    label={t(keys.seller.storeHighlightsArabicLabel)}
                    name={`highlight-${item.id}-ar`}
                  >
                    <TextInput
                      name={`highlight-${item.id}-ar`}
                      value={item.textAr}
                      onChange={(e) => handleChange(item.id, "textAr", e.target.value)}
                    />
                  </FormField>

                  {/* EN field — collapsed until expanded or already has content */}
                  {showEnField(item) ? (
                    <FormField
                      label={t(keys.seller.storeHighlightsEnglishLabel)}
                      name={`highlight-${item.id}-en`}
                      helperText={t(keys.seller.storeHighlightsEnglishHelper)}
                    >
                      <TextInput
                        name={`highlight-${item.id}-en`}
                        value={item.textEn}
                        onChange={(e) => handleChange(item.id, "textEn", e.target.value)}
                      />
                    </FormField>
                  ) : (
                    <Button
                      variant="link"
                      type="button"
                      onClick={() => handleExpandEn(item.id)}
                      className="self-start px-0 text-xs font-medium text-[color:var(--text-muted)] hover:bg-transparent hover:text-[color:var(--text-secondary)]"
                    >
                      + {t(keys.seller.storeHighlightsEnglishLabel)}
                    </Button>
                  )}

                  {/* Card footer: reorder controls (left) + remove (right) */}
                  <div className="flex items-center justify-between gap-2 border-t border-[color:var(--border-light)] pt-3">
                    <div className="flex items-center gap-1">
                      {/* Up / Down — icon-only with proper touch targets */}
                      <IconButton
                        variant="outline"
                        size="md"
                        onClick={() => moveItem(index, index - 1)}
                        disabled={index === 0}
                        ariaLabel={t(keys.seller.storeHighlightsMoveUp)}
                        className="text-[color:var(--text-muted)] transition hover:bg-[color:var(--surface-alt)] disabled:opacity-40"
                        icon={<ChevronUpIcon />}
                      />
                      <IconButton
                        variant="outline"
                        size="md"
                        onClick={() => moveItem(index, index + 1)}
                        disabled={index === items.length - 1}
                        ariaLabel={t(keys.seller.storeHighlightsMoveDown)}
                        className="text-[color:var(--text-muted)] transition hover:bg-[color:var(--surface-alt)] disabled:opacity-40"
                        icon={<ChevronDownIcon />}
                      />
                    </div>
                    <Button
                      variant="link"
                      type="button"
                      onClick={() => handleRemoveHighlight(item.id)}
                      className="px-0 text-xs font-semibold text-[color:var(--status-error)] hover:bg-transparent"
                    >
                      {t(keys.common.remove)}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button
            variant="ghost"
            type="button"
            onClick={handleAddHighlight}
            className="inline-flex items-center justify-center rounded-lg border border-[color:var(--border-light)] px-4 py-2 text-sm font-semibold text-[color:var(--text-primary)] transition hover:border-[color:var(--border-strong)]"
          >
            {t(keys.seller.storeHighlightsAdd)}
          </Button>
        </div>

        {/* Live preview — plain text, no box */}
        {items.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-semibold text-[color:var(--text-muted)]">
              {t(keys.seller.storeHighlightsPreviewTitle)}
            </p>
            <p className="text-xs text-[color:var(--text-secondary)]">
              <span className="font-medium">{t(keys.seller.storeHighlightsPreviewArabic)}: </span>
              {items.map((h) => h.textAr.trim()).filter(Boolean).join(" · ") || t(keys.common.empty)}
            </p>
            <p className="text-xs text-[color:var(--text-secondary)]">
              <span className="font-medium">{t(keys.seller.storeHighlightsPreviewEnglish)}: </span>
              {items.map((h) => h.textEn.trim() || h.textAr.trim()).filter(Boolean).join(" · ") || t(keys.common.empty)}
            </p>
          </div>
        ) : null}
      </div>

      {/* Floating save bar — page mode only; sheet mode uses a sticky footer in the parent */}
      {!isSheetMode && isDirty ? (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] end-0 start-0 z-20 border-t border-[color:var(--border-light)] bg-[color:var(--surface)]/90 px-4 py-3 backdrop-blur-sm lg:bottom-0">
          <div className="mx-auto flex max-w-4xl items-center justify-end">
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={() => void handleSubmit()}
              variant="primary"
              className="h-10 rounded-lg px-6 text-sm font-semibold disabled:opacity-60"
            >
              {isSubmitting ? t(keys.common.loading) : t(keys.common.save)}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
